import BaseBehavior from '../../../framework/base/base';
import { getLowerCaseUrlParams } from '../../../shared/utilities/getUrlParams';
import { objectToQueryString } from '../../../shared/utilities/objectToQueryString';
import { updateQueryStringParameter } from '../../../shared/utilities/updateQueryStringParameter';
import { showElement, hideElement } from '../../../shared/utilities/show-hide';
import { filterQuery, regEx } from '../../../shared/utilities/filterQuery';
import { screenSmallerThan } from '../../../shared/utilities/screenSize';
import { debounce } from '../../../shared/utilities/debounce';
import { dragScroll } from '../../templates/tab/dragScroll';
import { initBehaviors } from '../../../shared/utilities/behaviors';
import { onEffectEndEvent } from '../../../shared/utilities/effectsEndEventHandler';
import { initProvider } from '../../../shared/utilities/search-provider/searchProvider';
import trackSearchResult from '../../../shared/utilities/search-tracking/search-tracking';

export default class SearchResultPage extends BaseBehavior {
	constructor(element) {
		super(element);
		this.initDomCache();
		this.tabItems = element.querySelectorAll('.tab-item');
		this.init();
	}

	init() {
		const params = getLowerCaseUrlParams();
		const q = params.q ? params.q : '';
		let { category } = params;

		if (!category) {
			const tabContainer = this.element.querySelector('[data-activetab]');
			const activeTab =
				tabContainer && tabContainer.dataset.activetab
					? this.element.querySelector(`[data-id=${tabContainer.dataset.activetab}`)
					: null;
			if (activeTab && activeTab.dataset.id) {
				category = activeTab.dataset.id;
			} else {
				const firstTab = this.element.querySelector('li.tab-item:first-child');
				category = firstTab.dataset.id;
			}
		}
		const defaultActiveTab = this.element.querySelector(`li[data-id=${category}]`);
		this.moveActiveLine(defaultActiveTab);

		this.state = {
			query: '',
			correctedQuery: '',
			prevTab: '',
			activeTab: category,
			nextPage: {},
			totalCount: {}
		};
		this.fetchedInstrumentResult = false;
		this.addEventListeners();
		this.dom.searchInput.value = q;

		// need to pick google / bing search config
		this.config = JSON.parse(this.dom.searchConfig.innerHTML);
		this.searchProvider = this.config.provider;
		this.provider = initProvider(this.config);

		if (this.dom.instrumentConfig) {
			this.instrumentConfig = JSON.parse(this.dom.instrumentConfig.innerHTML);
		}
		this.tagsConfig = JSON.parse(this.dom.tagsConfig.innerHTML);
		// trigger search if q params has value
		q && this.triggerSearch(q);
		this.subscribe('updated-DPdata-ForSRP', data => {
			this.updatedSkipForDP = data.skip;
		});
	}

	addSubscription() {
		this.subscribe(`/cb-dropdown/change`, data => {
			const dpContainer = this.element.querySelector('[data-ref="dynamic-pricing2__dpContainer"]');
			const { guid } = dpContainer.dataset;
			if (
				data.dropdownId === 'priceFilter' ||
				data.dropdownId === 'proficiencyFilter' ||
				data.dropdownId === 'countryFilter'
			) {
				this.publish('dynamic-pricing/prepare-header-info');
			}
			if (
				data.dropdownId &&
				guid === data.targetID &&
				(data.dropdownId === 'assetsFilter' ||
					data.dropdownId === 'priceFilter' ||
					data.dropdownId === 'proficiencyFilter' ||
					data.dropdownId === 'countryFilter')
			) {
				const currentTabId = `content_${this.state.activeTab}`;
				if (this.dom.searchInput.value.length) {
					this.getInstruments(currentTabId);
				}
			}
		});
	}

	triggerSearch(query) {
		const q = filterQuery(query, regEx.GOOGLE_SEARCH);
		if (!q || this.state.query === q) return;
		this.state.correctedQuery = '';
		this.dom.clearSearch.classList.remove('v2-not-visible');

		// updating url params with searched query
		const newUrl = updateQueryStringParameter(window.location.href, 'q', q);
		window.history.pushState({ path: newUrl }, '', newUrl);

		// updateing search query state
		this.state.query = q;
		this.fetchedInstrumentResult = false;
		// search results needs to be updated for all tabs when active tab is Instruments
		// else should be loaded for all tabs except for
		this.updateSearchResults(query);
	}

	updateSearchResults(query, loadShowMore = false) {
		// hide content of active tab to show only loader
		// when loading more content not removing previous data
		if (!loadShowMore) {
			hideElement(this.dom.result_container);
			hideElement(this.dom.showMore);
		}
		showElement(this.dom.loader);

		this.makeURLRequests(query, loadShowMore)
			.then(data => {
				data.forEach((result, index) => {
					/* eslint-disable prettier/prettier */
					const promotionsCount = result?.promotions?.length || 0;
					const promotions = result.promotions && !loadShowMore
						? result.promotions.map(item => { 
							const description = item.bodyLines[0].title || ''
							return {
								link: item.link || '',
								formattedUrl: item.link || '',
								title: item.title || '',
								htmlSnippet: description !== "undefined" ? description  : ''
						  }})
						: [];
					const items = [...promotions, ...(result.items || [])];

                    

					const obj = {
						query,
						totalCount: Number(result.searchInformation.totalResults)+promotionsCount || 0,
						items					
					};

					if(this.googleSearchItems[index].dataset.id === 'all') {
						const resultsCount = +result.searchInformation.totalResults;
						trackSearchResult(query, resultsCount, 'All');
					}

					if (!this.state.correctedQuery) {
						this.state.correctedQuery = result?.spelling?.correctedQuery.split(' ')[0] || '';
					}
					const tab = loadShowMore
						? this.state.activeTab
						: this.googleSearchItems[index].dataset.id;

					// finding next Offset for bing / Google API
					let nextOffset = 0;
					if (this.searchProvider === 'bing') {
						nextOffset = this.state.nextPage[tab] + Number(this.config.num);				  

					} else {
					  nextOffset = result.queries.nextPage ? result.queries.nextPage[0].startIndex : 0;
					}
					obj.next = nextOffset;

					this.updateActiveTab(obj, loadShowMore, tab);
				});
				// update corrected query
				this.handleQuerySuggestion();
			})
			.catch((err) => {
				// handle error state
				// eslint-disable-next-line no-console
				console.log(err);
			})
			.finally(() => {
				hideElement(this.dom.loader);
				this.renderTabContent(this.state.activeTab);
				if (!(this.state.activeTab === 'instruments')) {
					showElement(this.dom.result_container);
				}
			});
	}

	handleQuerySuggestion() {
		if (!this.dom.helperText) return;
		if (this.state.correctedQuery && this.state.activeTab !== 'instruments') {
			this.dom.helperText.querySelector('span').innerText = this.state.correctedQuery + ' ?';
			this.dom.helperText.classList.remove('v2-hidden');
		} else {
			this.dom.helperText.classList.add('v2-hidden');
		}
	}

	makeURLRequests(query, loadShowMore) {
		this.googleSearchItems = Array.from(this.tabItems).filter(item => {
			return !item.dataset.target.includes('instruments');
		});
		// make array of url based on data filter.
		const urls = [];
		let queryWithFilter = "";
		this.googleSearchItems.forEach((item, index) => {
			// in case of load more results , make request url for only one tab
			if (loadShowMore && item.dataset.id !== this.state.activeTab) {
				return;
			}
			const lang = document.documentElement.lang || 'en';
			const sortType = item.dataset.sort;
			if (this.searchProvider === 'bing') {
				queryWithFilter =  item.dataset.cnfilter ? `${query}+${item.dataset.cnfilter}` : query;
			} else {
			 queryWithFilter = query + ` ${item.dataset.filter}`;
			}
			// query += ` ${item.dataset.filter}`;
			const defaultOffset = this.searchProvider === 'bing' ? 0 : 1;
			const start = loadShowMore ? this.state.nextPage[this.state.activeTab] :defaultOffset; 
			const lr = `lang_${lang}`;
			const { url } = this.provider.getRequest({query:queryWithFilter, start, lr,sortType });
			urls[index] = url;			
		});
	    const option = this.provider.getOptions();
		const requests = urls.filter(url => url).map(url => fetch(url,option));
		return this.fetchSearchResult(requests);
	}

	fetchSearchResult(requests) {
		return Promise.all(requests)
			.then(responses => {
				// Get a JSON object from each of the responses
				return Promise.all(
					responses.map(response => {
						return response.json();
					})
				);
			})
			.then(data => {
				
				return this.provider.parseResponse(data);
			})
			.catch(error => {
				// eslint-disable-next-line no-console
				console.log(error);
			});
	}

	addEventListeners() {
		let timer = null;
		const handleSearch = query => {
			if (query.length === 0) {
				this.resetSearch();
			} else {
				// TODO : restict query through regx
				this.triggerSearch(query);
			}
		};

		dragScroll(this.element);
		this.addEventListener(this.dom.searchInput, 'keyup', e => {
			const query = e.target.value;
			clearTimeout(timer);
			timer = setTimeout(handleSearch, 500, query);
		});
		
		this.addEventListener(this.dom.clearSearch, 'click', () => {
			this.resetSearch();
		});

		this.addEventListener(this.dom.showMore, 'click', e => {
			this.fetchMoreContent(e);
		});
		Array.from(this.tabItems).forEach((tab, i) => {
			const tabName = tab.dataset.id;
			this.state.nextPage[tabName] = 0;
			this.addEventListener(tab, 'click.tab' + i, e => {
				e.preventDefault();
				if (!e.target.closest('.active')) {
					this.setActiveTab(tabName);
					this.renderTabContent(this.state.activeTab);
				}
			});
		});
		this.addEventListener(
			window,
			'resize',
			debounce(() => {
				Array.from(this.tabItems).forEach(tab => {
					if (tab.classList.contains('active')) {
						this.moveActiveLine(tab);
					}
				});
			}, 250)
		);

		// Begin:: Ins add event listenr for icon and load more
		this.addEventListener(this.dom.result_container, 'click', e => this.handleInstrumentActions(e));

		this.addEventListener(this.dom.helperText, 'click', () => {
			const query = this.state.correctedQuery;
			this.dom.searchInput.value = query;
			this.triggerSearch(query);
		});
	}

	// Begin:: Ins
	handleInstrumentActions(e) {
		const icon = e.target.closest("span[data-qa='info-icon']");
		const mobileContainer = e.target.closest('.table-mobile');
		const headerIcon = e.target.closest('.dynamic-pricing__headerIcon');
		if (mobileContainer && e.target.type === 'checkbox') {
			const self = e.target;
			Array.from(mobileContainer.querySelectorAll('input[type="checkbox"]')).forEach(check => {
				if (check !== self) {
					// eslint-disable-next-line
					check.checked = false;
				}
			});
		}

		const loadmore = e.target.closest('div[data-ref="dynamic-pricing2__loadMore"]');
		if(loadmore) {
			this.publish('/search-result/loadmore', {
				event:e,
				top: +this.instrumentConfig.top,
				skip: +this.updatedSkipForDP ? +this.updatedSkipForDP : +this.instrumentConfig.skip,
				url: this.instrumentUrl,
				query: this.state.query,
				source: 'searchresultpage'
			});
		}
		if(icon || headerIcon) {
			this.publish('/search-result/openInfoPopup', {event: e, source: 'searchresultpage'});
		}
		
		// End:: Ins
	}

	setActiveTab(tabName) {
		// update url parmas
		const newUrl = updateQueryStringParameter(window.location.href, 'category', tabName);
		window.history.pushState({ path: newUrl }, '', newUrl);
		this.state.prevTab = this.state.activeTab;
		this.state.activeTab = tabName;
	}

	fetchInstrumentSearchResults() {
		let countryVal;
		if (!filterQuery(this.state.query, regEx.INSTRUMENT_SEARCH)) {
			return Promise.reject(Error('Not valid a search query'));
		}
		const { serviceUrl } = this.instrumentConfig;

		const assetFilter = this.dom.instrument_section.querySelector('[data-ref="search-result-page__assetsFilter"]');
		const assetValue = assetFilter.querySelector('.anchor-text--optional').dataset.value || '';


		const priceFilter = this.dom.instrument_section.querySelector('[data-ref="search-result-page__priceFilter"]');
		const priceValue = priceFilter && priceFilter.querySelector('.anchor-text--optional').dataset.value || this.instrumentConfig.commissiongroupids;

		const countryFilter = this.dom.instrument_section.querySelector('[data-ref="search-result-page__countryFilter"]'); 
		if (countryFilter) {
			const proficiencyFilter = this.dom.instrument_section.querySelector('[data-ref="search-result-page__proficiencyFilter"]'); 
			const val =
				(proficiencyFilter &&
					proficiencyFilter.querySelector('.anchor-text--optional').dataset.value) ||
				'retail';
			if (val.toLowerCase() === 'retail') {
				countryVal =
					countryFilter.querySelector('.anchor-text--optional').dataset.value || '';
			}
		}

		if (assetValue.startsWith('asset-') && !assetValue.includes(',')) {
			this.publish('/search-result/show-static-message-dp', {assetId: assetValue});
			/*eslint-disable */
			return Promise.resolve('staticAsset');
		} 
		const params = {
			top: this.instrumentConfig.top,
			skip: this.instrumentConfig.skip,
			assetids: assetValue || '', // this is asset filter needs to picked from asset dropdown,
			commissiongroupids: priceValue,
			namecategoryid: this.instrumentConfig.namecategoryid ? this.instrumentConfig.namecategoryid : this.dom.dpContainer.dataset.namecategoryid,
			keywords: this.state.query,
			ismobile: screenSmallerThan('sm'),
			lang: this.instrumentConfig.culture,
			env: this.instrumentConfig.environment,
			db: this.instrumentConfig.database
		};
		// setting data attribute, so that it can be used in DP2
		const dpContainer = this.element.querySelector('[data-ref="dynamic-pricing2__dpContainer"]');
		dpContainer.dataset.commissiongroupids = params.commissiongroupids;
		if(countryVal) {
			params.marketcode = countryVal
		}
		const showInfoCol =
			dpContainer.querySelector('th[data-columntype="InfoColumnType"]') !== null;
		params.getinfodata = showInfoCol;
		const url = `${serviceUrl}?${objectToQueryString(params)}`;
		this.instrumentUrl = url;
		return fetch(url)
			.then(response => {
				if (!response.ok) {
					throw Error(response.statusText);
				}
				return response.json();
			})
			.then(responseJson => responseJson);
	}

	renderTabContent(tabName) {
		const currentTab = this.dom[`tab_${tabName}`];
		const currentTabId = currentTab.dataset.target;
		const isInstrumentTab = this.state.activeTab === 'instruments';

		this.handleQuerySuggestion();

		// if switch from instrument tab
		if (this.state.prevTab === 'instruments') {
			if(screenSmallerThan('sm')) {
				this.dom.content_instruments.querySelector(
					'.table-mobile'
				).innerHTML = this.dom.result_container.querySelector('.table-mobile')?.innerHTML;
			}
			else {
				this.updatedSkipForDP = +this.instrumentConfig.skip;
			}
		}

		// this condition is for show/hide asset filter and rte text.
		if (this.dom.instrument_section) {
			if (this.dom.instrument_section.innerHTML === '' && this.dom.instrument_section_li) {
				this.dom.instrument_section.innerHTML = this.dom.instrument_section_li.innerHTML;
				initBehaviors({context: this.dom.instrument_section });
				this.addSubscription();
			}
			if (isInstrumentTab) {
				showElement(this.dom.instrument_section);
			} else {
				hideElement(this.dom.instrument_section);
			}
		}
		const searchInstruments = isInstrumentTab && !this.fetchedInstrumentResult;

		this.tabItems.forEach(tabItem => {
			tabItem.classList.remove('active');
			const tabId = tabItem.dataset.target;
			// loading table skeleton only when data is fetched to avoid rendering empty tabel
			if (tabId === currentTabId) {
				if (!searchInstruments) {
					this.dom.result_container.innerHTML = this.dom[tabId].innerHTML;
					if (isInstrumentTab) {
						this.dom.content_instruments.querySelector('.table-mobile').innerHTML = '';
					}
				}
			}
		});

		this.moveActiveLine(currentTab);
		this.toggleShowMore();
		// if active tab is instruments , lazy load its content
		if (searchInstruments) {
				hideElement(this.dom.result_container);
				if (this.state.query !== '') {
					showElement(this.dom.loader);
					this.getInstruments(currentTabId);
				}
		} else {
			showElement(this.dom.result_container);
		}
	}

	toggleShowMore() {
		// check if nextPage is available for current active tab and less than limit 100
		let showLoadMoreBtn = false;
		if (this.searchProvider === 'bing') {
			const totalMatches = this.state.totalCount[this.state.activeTab];
			const count = this.state.nextPage[this.state.activeTab] // count of showmore clicks * count of records config
			if (count < totalMatches) {
				showLoadMoreBtn = true;
			}			 
		} else {
			const nextIndex = this.state.nextPage[this.state.activeTab];
			const limit = nextIndex - 1 + Number(this.config.num) || 100 ;
			const maxRecords = Number(this.config.max);
			if (nextIndex > 0 && limit <= maxRecords) {
				showLoadMoreBtn = true;
			} 
	   }
	   if (showLoadMoreBtn) {
			this.dom.showMore.classList.remove('v2-hidden');
	   } else {
			this.dom.showMore.classList.add('v2-hidden'); 
	   }
	}

	getInstruments(currentTabId) {
		this.fetchInstrumentSearchResults()
			.then(data => {
				if(data !== 'staticAsset') {
					// eslint-disable-next-line no-console
					console.log('got data from Instruments 🔎 published ');
					this.fetchedInstrumentResult = true;
					this.updatedSkipForDP = this.instrumentConfig.skip;
					// publish data to dynamic pricing
					this.publish('/search-result/updateresults', {
						html: data,
						extraData: {
							searchResultPage: true,
							top: +this.instrumentConfig.top || 5,
							skip: +this.instrumentConfig.skip || 0,
							total: +data.TotalCount,
							url : this.instrumentUrl,
							query: this.state.query
						}
					});
				}	
			})
			.catch(e => {
				// eslint-disable-next-line no-console
				console.error(e);
			})
			.finally(() => {
				hideElement(this.dom.loader);
				// show instrument search container
				if(this.dom.result_container && this.dom[currentTabId]) {
					this.dom.result_container.innerHTML = this.dom[currentTabId]?.innerHTML;
					this.cleanup();
					showElement(this.dom.result_container);
					initBehaviors({context: this.dom.result_container });
					this.dom[currentTabId].querySelector('.table-mobile').innerHTML = '';
				}
			});
	}

	updateActiveLineDimensions(tab, line) {
		/* comment to remove - adding tabLeftOffset here , because in mobile it calculate value before move-in animation
				 and produces wrong results. */
		const tabLeftOffset = this.element.querySelector('.tab-list').getBoundingClientRect().left;
		const { scrollLeft } = document.querySelector('.dragscroll');

		line.style.left = scrollLeft + tab.getBoundingClientRect().left - tabLeftOffset + 'px';
		line.style.width = tab.getBoundingClientRect().width + 'px';
	}

	moveActiveLine(tab) {
		const activeTabLine = this.element.querySelector('.active-line');
		onEffectEndEvent(tab, () => this.updateActiveLineDimensions(tab, activeTabLine));
		tab.classList.add('active');
		this.updateActiveLineDimensions(tab, activeTabLine);
	}

	updateActiveTab({ items, totalCount, next }, loadShowMore, tab) {
		const currentTab = this.dom[`tab_${tab}`];
		const currentContainer = currentTab.dataset.target;
		this.state.nextPage[tab] = next;
		this.state.totalCount[tab] = totalCount;
		const countBox = currentTab.querySelector('.count-box');
		const { showTag } = currentTab.dataset;
		let markup = '';

		if (countBox) {
			countBox.innerText = totalCount;
		}

		if (items.length === 0) {
			markup = this.dom[`noResults_${tab}`] ? this.dom[`noResults_${tab}`].innerHTML : '';
		} else {
			markup = items
				.map(item => {
					// find tag
					let tag = '';
					const isPromoted = item.kind !== 'customsearch#result';

					this.tagsConfig.tags.forEach(key => {
						if (key.type === 'promoted' && isPromoted) {
							tag = key.tag;
						}
						if (key.type === 'page' && item.link?.match(key.url) && showTag) {
							tag = key.tag;
						}
					});

					item.htmlSnippet = item.htmlSnippet?.replace('<br>', '') || '';
					return `<a class="tab-result v2-pav15" href=${item.link}>
						<div class="tab-result__header">
						<h4 class="title">${item.title}</h4>${tag && `<span class="tag">${tag}</span>`}
						</div>
						<p class="tab-result__desc v2-hide-empty">${item.htmlSnippet}</p>
						<span class="v2-btn v2-btn--link tab-result__link">${item.formattedUrl}</span>
						
					</a>
					`;
				})
				.join('');
		}

		if (loadShowMore) {
			this.dom[currentContainer].insertAdjacentHTML('beforeend', markup);
		} else {
			this.dom[currentContainer].innerHTML = markup;
		}
	}

	fetchMoreContent(e) {
		e.preventDefault();
		hideElement(this.dom.showMore);
		this.updateSearchResults(this.state.query, true);
	}

	resetSearch() {
		this.dom.searchInput.value = '';
		this.state.query = '';
		this.dom.clearSearch.classList.add('v2-not-visible');
		this.dom.searchInput.focus();
	}

	cleanup() {
		this.publish('clean-srp-events');
	} 
}
